import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AddressBook, CaretDown, CaretUp, FileText, Plus, Trash, Funnel, X, ChatCircleText, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ContactChatDialog } from "@/components/admin/ContactChat";

const fmt = (c) => `$${((c || 0) / 100).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const emptyForm = { name: "", email: "", phone: "", address: "" };

export const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(`${dateStr}T00:00:00`) - today) / 86400000);
};
export const dueLabel = (dateStr) => {
  const d = daysUntil(dateStr);
  if (d === null) return "";
  if (d === 0) return "due today";
  return d > 0 ? `due in ${d} day${d === 1 ? "" : "s"}` : `${-d} day${d === -1 ? "" : "s"} past due`;
};
export const validLabel = (dateStr) => {
  const d = daysUntil(dateStr);
  if (d === null) return "";
  if (d === 0) return "valid through today";
  return d > 0 ? `valid ${d} more day${d === 1 ? "" : "s"}` : `expired ${-d} day${d === -1 ? "" : "s"} ago`;
};

export function CustomersCard({ customers, onChanged, onFilter, onNewDoc, onView }) {
  const [chat, setChat] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => { try { const { data } = await api.get("/admin/customers"); onChanged(data); } catch { /* noop */ } };
  useEffect(() => { if (open) reload(); }, [open]); // eslint-disable-line

  const addCustomer = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/customers", { name: form.name, email: form.email || null, phone: form.phone || null, address: form.address || null });
      setForm(emptyForm);
      await reload();
      toast.success("Customer added");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const openDetail = async (c) => {
    if (expandedId === c.id) { setExpandedId(null); setDetail(null); setHistory(null); return; }
    setExpandedId(c.id);
    setDetail({ name: c.name, email: c.email || "", phone: c.phone || "", address: c.address || "", notes: c.notes || "" });
    setHistory(null);
    try { const { data } = await api.get(`/admin/customers/${c.id}/documents`); setHistory(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveDetail = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/customers/${expandedId}`, detail);
      await reload();
      toast.success("Customer updated — future documents auto-fill the new info");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeCustomer = async (c) => {
    if (!window.confirm(`Remove "${c.name}" from the customer list? Their existing quotes/invoices are untouched.`)) return;
    try { await api.delete(`/admin/customers/${c.id}`); setExpandedId(null); await reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const payChip = (d) => {
    if ((d.kind || "invoice") !== "invoice") return null;
    if (d.status === "draft") return { label: "draft", bg: "#FEF3C7", color: "#92400E" };
    if (d.payment_status === "paid") return { label: "paid", bg: "#DCFCE7", color: "#166534" };
    if (d.overdue) return { label: "overdue", bg: "#FEE2E2", color: "#B91C1C" };
    if (d.payment_status === "partial") return { label: "part-paid", bg: "#FEF3C7", color: "#92400E" };
    return { label: "unpaid", bg: "#F4F4F5", color: "#000000" };
  };

  return (
    <div className="card" data-testid="customers-card">
      <button type="button" className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer p-0" onClick={() => setOpen((o) => !o)} data-testid="customers-toggle">
        <div className="flex items-center gap-2">
          <AddressBook size={22} weight="duotone" color="#7C3AED" />
          <div className="text-left">
            <h3 className="text-lg font-bold font-display">Customers ({customers.length})</h3>
            <p className="text-xs text-[#000000]">One record per customer — click a name to edit their info and see their full quote/invoice history</p>
          </div>
        </div>
        {open ? <CaretUp size={18} color="#52525B" /> : <CaretDown size={18} color="#52525B" />}
      </button>
      {open && (
        <div className="mt-4 flex flex-col gap-3">
          <form onSubmit={addCustomer} className="grid grid-cols-12 gap-2 items-end">
            <label className="col-span-12 sm:col-span-3 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Name
              <input data-testid="customer-form-name" className="input-base" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label className="col-span-6 sm:col-span-3 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Email
              <input data-testid="customer-form-email" className="input-base" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            <label className="col-span-6 sm:col-span-2 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Phone
              <input data-testid="customer-form-phone" className="input-base" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </label>
            <label className="col-span-12 sm:col-span-2 text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Address
              <input data-testid="customer-form-address" className="input-base" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </label>
            <div className="col-span-12 sm:col-span-2">
              <button type="submit" className="btn-primary text-sm w-full" disabled={busy} data-testid="customer-form-save">Add customer</button>
            </div>
          </form>

          <div className="flex flex-col gap-1.5">
            {customers.map((c, i) => (
              <div key={c.id} className="rounded-xl border border-[#C4C8CE] bg-white/60 overflow-hidden">
                <div className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white" data-testid={`customer-row-${i}`}
                  onClick={() => openDetail(c)}>
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold text-sm">{c.name}</span>
                    {c.email && <span className="text-xs text-[#000000] ml-2">{c.email}</span>}
                  </div>
                  <span className="text-xs text-[#000000] whitespace-nowrap">{c.quotes} quote{c.quotes === 1 ? "" : "s"} · {c.invoices} invoice{c.invoices === 1 ? "" : "s"}{c.drafts > 0 ? ` · ${c.drafts} draft` : ""}</span>
                  <span className="text-sm font-bold whitespace-nowrap"
                    style={{ color: c.balance_cents > 0 ? "#92400E" : c.invoiced_cents > 0 ? "#0A6B2C" : "#075985" }}
                    title={c.invoiced_cents > 0 ? `Invoiced ${fmt(c.invoiced_cents)} · paid ${fmt(c.paid_cents)}` : "No invoices yet"}
                    data-testid={`customer-money-badge-${i}`}>
                    {c.balance_cents > 0 ? `${fmt(c.balance_cents)} due`
                      : c.invoiced_cents > 0 ? "settled"
                        : c.quotes_sent > 0 ? "quote sent"
                          : c.quotes > 0 ? "quoted"
                            : "—"}
                  </span>
                  <button type="button" className="btn-secondary text-xs py-0.5 px-2 flex items-center gap-1"
                    data-testid={`customer-new-quote-${i}`} title={`New quote for ${c.name} — pre-filled`}
                    onClick={(e) => { e.stopPropagation(); onNewDoc("quote", c); }}>
                    <FileText size={11} weight="bold" /> quote
                  </button>
                  <button type="button" className="btn-secondary text-xs py-0.5 px-2 flex items-center gap-1"
                    data-testid={`customer-new-invoice-${i}`} title={`New invoice for ${c.name} — pre-filled`}
                    onClick={(e) => { e.stopPropagation(); onNewDoc("invoice", c); }}>
                    <Plus size={11} weight="bold" /> invoice
                  </button>
                  {expandedId === c.id ? <CaretUp size={14} color="#52525B" /> : <CaretDown size={14} color="#52525B" />}
                </div>

                {expandedId === c.id && detail && (
                  <div className="border-t border-[#C4C8CE] bg-[#ECEDEF] p-3 flex flex-col gap-3" data-testid="customer-detail">
                    <div className="flex items-center gap-2 flex-wrap">
                      {detail.phone && (
                        <button type="button" className="btn-primary text-xs flex items-center gap-1" data-testid={`customer-chat-text-${i}`}
                          onClick={() => setChat({ id: c.id, name: detail.name || c.name, phone: detail.phone, email: detail.email, channel: "sms" })}>
                          <ChatCircleText size={13} /> Text
                        </button>
                      )}
                      {detail.email && (
                        <button type="button" className="btn-primary text-xs flex items-center gap-1" data-testid={`customer-chat-email-${i}`}
                          onClick={() => setChat({ id: c.id, name: detail.name || c.name, phone: detail.phone, email: detail.email, channel: "email" })}>
                          <EnvelopeSimple size={13} /> Email
                        </button>
                      )}
                      {(detail.phone || detail.email) && (
                        <span className="text-[11px] text-[#000000]">opens your full text & email history with {detail.name || c.name}</span>
                      )}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Name
                        <input data-testid="customer-detail-name" className="input-base" value={detail.name} onChange={(e) => setDetail({ ...detail, name: e.target.value })} />
                      </label>
                      <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Email
                        <input data-testid="customer-detail-email" className="input-base" type="email" value={detail.email} onChange={(e) => setDetail({ ...detail, email: e.target.value })} />
                      </label>
                      <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Phone
                        <input data-testid="customer-detail-phone" className="input-base" value={detail.phone} onChange={(e) => setDetail({ ...detail, phone: e.target.value })} />
                      </label>
                      <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Address
                        <input data-testid="customer-detail-address" className="input-base" value={detail.address} onChange={(e) => setDetail({ ...detail, address: e.target.value })} />
                      </label>
                    </div>
                    <label className="text-xs uppercase tracking-[0.1em] text-[#000000] flex flex-col gap-1">Notes (internal — never printed)
                      <input data-testid="customer-detail-notes" className="input-base" value={detail.notes} onChange={(e) => setDetail({ ...detail, notes: e.target.value })} />
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      <button type="button" className="btn-primary text-sm" disabled={busy} onClick={saveDetail} data-testid="customer-detail-save">{busy ? "Saving…" : "Save customer"}</button>
                      <button type="button" className="btn-secondary text-sm flex items-center gap-1" onClick={() => onFilter(c.name)} data-testid="customer-detail-filter">
                        <Funnel size={13} /> Show in documents list
                      </button>
                      <button type="button" className="btn-ghost text-sm text-[#FF5100] flex items-center gap-1" onClick={() => removeCustomer(c)} data-testid="customer-detail-delete">
                        <Trash size={13} /> Remove
                      </button>
                      <button type="button" className="btn-ghost text-sm ml-auto" onClick={() => { setExpandedId(null); setDetail(null); }} data-testid="customer-detail-close">
                        <X size={14} /> Close
                      </button>
                    </div>

                    <div>
                      <div className="text-xs uppercase tracking-[0.12em] text-[#000000] mb-1">Document history — click one to view or print it</div>
                      {history === null ? (
                        <div className="text-xs text-[#000000] italic">Loading…</div>
                      ) : history.length === 0 ? (
                        <div className="text-xs text-[#000000] italic">No quotes or invoices yet.</div>
                      ) : (
                        <div className="flex flex-col gap-1" data-testid="customer-history">
                          {history.map((d) => {
                            const chip = payChip(d);
                            return (
                              <div key={d.id}
                                className="flex items-center gap-3 text-xs bg-white rounded-lg border border-[#C4C8CE] px-2 py-1.5 cursor-pointer hover:border-[#3F6212] hover:bg-[#F7FBEF] transition-colors"
                                title={`View or print ${(d.kind || "invoice")} ${d.invoice_number}`}
                                onClick={() => onView && onView(d)}
                                data-testid={`customer-history-${d.invoice_number}`}>
                                <span className="font-mono font-bold text-[#3F6212]">{d.invoice_number}</span>
                                <span className="uppercase font-bold" style={{ color: (d.kind || "invoice") === "quote" ? "#007A8F" : "#3F6212" }}>{d.kind || "invoice"}</span>
                                <span className="flex-1 truncate text-[#000000]">{(d.items || []).map((it) => it.name || it.description).filter(Boolean).join(", ")}</span>
                                <span className="font-bold">{fmt(d.total_cents)}</span>
                                {chip && <span className="chip text-[10px] font-bold" style={{ background: chip.bg, color: chip.color }}>{chip.label}</span>}
                                {(d.kind || "invoice") === "invoice" && d.due_date && d.status !== "draft" && d.payment_status !== "paid" && (
                                  <span className="font-bold whitespace-nowrap" style={{ color: "#000000" }} data-testid={`history-due-${d.invoice_number}`}>{dueLabel(d.due_date)}</span>
                                )}
                                {(d.kind || "invoice") === "quote" && d.valid_until && d.status !== "draft" && (
                                  <span className="font-bold whitespace-nowrap" style={{ color: "#000000" }}>{validLabel(d.valid_until)}</span>
                                )}
                                {d.status === "draft" && (d.kind || "invoice") === "quote" && <span className="chip text-[10px] font-bold" style={{ background: "#FEF3C7", color: "#92400E" }}>draft</span>}
                                <span className="text-[#000000]">{new Date(d.created_at).toLocaleDateString()}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {customers.length === 0 && <div className="text-[#000000] text-sm italic">No customers yet — add one above, or they appear automatically when you create a quote or invoice, or when someone requests a booking.</div>}
          </div>
        </div>
      )}
      {chat && <ContactChatDialog contact={chat} defaultChannel={chat.channel} onClose={() => setChat(null)} />}
    </div>
  );
}
